import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import {
  GetGroupVisitPatientsEnrollmentStatusEnum,
  GroupVisitControllerApi,
} from "@/services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  getXtenantId,
  useBaseData,
} from "@/utils/utils";

export const GROUP_VISIT_PATIENTS_QUERY_KEY = "group-visit-patients";

/** Newest enrolment first, using the DB column the API sorts on. */
const GROUP_PATIENTS_DEFAULT_SORT_BY = "enrolled_date";
const GROUP_PATIENTS_DEFAULT_SORT_DIRECTION = "desc";

type Params = {
  groupVisitId?: string;
  pagination: DGT_Pagination;
  searchKey?: string;
  enrollmentStatus?: GetGroupVisitPatientsEnrollmentStatusEnum;
  sortBy?: string;
  sortDirection?: string;
  enabled?: boolean;
};

/**
 * Patients enrolled in a group. Search, status filtering and paging are
 * server-side.
 *
 * @author Rajkumar Rathod
 */
export const useGroupVisitPatients = <T>({
  groupVisitId,
  pagination,
  searchKey,
  enrollmentStatus,
  sortBy = GROUP_PATIENTS_DEFAULT_SORT_BY,
  sortDirection = GROUP_PATIENTS_DEFAULT_SORT_DIRECTION,
  enabled = true,
}: Params): useBaseData<T> => {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey,
    prevPage,
  });
  const pageSize = getPageSize(searchKey, pagination);

  return useQuery({
    queryKey: [
      GROUP_VISIT_PATIENTS_QUERY_KEY,
      groupVisitId,
      pageIndex,
      pageSize,
      searchKey,
      enrollmentStatus,
      sortBy,
      sortDirection,
    ],
    enabled: enabled && !!groupVisitId,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      new GroupVisitControllerApi(getConfigObject())
        .getGroupVisitPatients(
          groupVisitId as string,
          pageIndex, // page
          pageSize, // size
          sortBy, // sortBy
          sortDirection, // sortDirection
          searchKey ? searchKey : undefined, // searchString
          enrollmentStatus, // enrollmentStatus
          getXtenantId() // xTENANTID
        )
        .then((res) => res?.data),
  });
};
